import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import type { Editor } from '@tiptap/react';
import { useCallback, useState } from 'react';

import { InsertTableModalErrorResponse } from '../../components/modals';

export interface TableModalReturn {
  isInsertTableModalOpen: boolean;
  tableRows: string;
  tableCols: string;
  tableErrors: InsertTableModalErrorResponse;

  openInsertTableModal: () => void;
  closeInsertTableModal: () => void;
  setTableRows: (rows: string) => void;
  setTableCols: (cols: string) => void;
  setTableErrors: (errors: InsertTableModalErrorResponse) => void;

  handleInsertTable: () => void;
}

/**
 * useTableModal
 * Unified hook for table modal state management + business logic
 */
export function useTableModal(editor: Editor | null): TableModalReturn {
  const [isInsertTableModalOpen, setIsInsertTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState('3');
  const [tableCols, setTableCols] = useState('3');
  const [tableErrors, setTableErrors] = useState<InsertTableModalErrorResponse>({});

  const openInsertTableModal = useCallback(() => setIsInsertTableModalOpen(true), []);
  const closeInsertTableModal = useCallback(() => setIsInsertTableModalOpen(false), []);

  const handleInsertTable = useCallback(() => {
    if (!editor) {
      return;
    }

    const rows = tableRows;
    const cols = tableCols;

    if (!rows) {
      setTableErrors({ value: [ERROR_MESSAGES.TABLE.REQUIRED_ROWS] });
      return;
    }
    if (!cols) {
      setTableErrors({ value: [ERROR_MESSAGES.TABLE.REQUIRED_COLS] });
      return;
    }

    const parsedRows = parseInt(rows, 10);
    const parsedCols = parseInt(cols, 10);
    if (parsedRows < 1 || parsedCols < 1) {
      setTableErrors({
        value: [ERROR_MESSAGES.TABLE.INVALID_COUNT],
      });
      return;
    }

    setTableErrors({});

    editor.view.dom.focus({ preventScroll: true });
    editor
      .chain()
      .insertTable({ rows: parsedRows, cols: parsedCols, withHeaderRow: false })
      .command(({ tr, state }) => {
        const paragraphNode = state.schema.nodes.paragraph.create();
        const { $to } = tr.selection;
        const posAfterTable = $to.after(1);
        tr.insert(posAfterTable, paragraphNode);
        return true;
      })
      .run();

    closeInsertTableModal();
    setTableRows('3');
    setTableCols('3');
    setTableErrors({});
  }, [editor, tableRows, tableCols, closeInsertTableModal]);

  return {
    isInsertTableModalOpen,
    tableRows,
    tableCols,
    tableErrors,

    openInsertTableModal,
    closeInsertTableModal,
    setTableRows,
    setTableCols,
    setTableErrors,

    handleInsertTable,
  };
}
